import { tokens } from "@/shared/design-system/tokens";
import styled from "@emotion/styled";
import Tooltip from "../ui/Tooltip/Tooltip";
export const FormFlowContainer = styled.div`
  display: grid;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;
export const FormFlowStepper = styled.nav`
  display: flex;
  width: 100%;
  isolation: isolate;
  overflow: visible;
  @media (max-width: 820px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;
export const FormFlowStepTooltip = styled(Tooltip)<{ $isFirst: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  margin-left: ${({ $isFirst }) => ($isFirst ? "0" : "-10px")};

  > button {
    width: 100%;
    margin-left: 0;
  }
`;
export const FormFlowStepIconSlot = styled.span`
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;
export const FormFlowStepButton = styled.button<{
  $active: boolean;
  $completed: boolean;
  $isFirst: boolean;
  $isLast: boolean;
  $stackIndex: number;
}>`
  --step-fill: ${({ $active, $completed }) => {
    if ($active) {
      return "rgba(216, 233, 241, 0.94)";
    }
    if ($completed) {
      return "rgba(142, 188, 207, 0.86)";
    }
    return "rgba(67, 112, 132, 0.72)";
  }};
  --step-stroke: ${({ $active }) =>
    $active ? "rgba(255, 255, 255, 0.92)" : "rgba(11, 62, 95, 0.96)"};
  min-height: 56px;
  border: 0;
  background: transparent;
  color: ${tokens.colors.blue[900]};
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  flex: 1 1 0;
  min-width: 0;
  margin-left: ${({ $isFirst }) =>
    $isFirst ? "0" : "-10px"};
  padding: 0 ${({ $isLast }) => ($isLast ? "36px" : "48px")} 0
    ${({ $isFirst }) => ($isFirst ? "56px" : "68px")};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.semibold};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  position: relative;
  filter: ${({ $active }) =>
    $active ? "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.24))" : "none"};
  opacity: 1;
  transition:
    color ${tokens.transitions.fast},
    filter ${tokens.transitions.fast},
    opacity ${tokens.transitions.fast};
  z-index: ${({ $active, $stackIndex }) => ($active ? 10 : $stackIndex)};
  &:hover:not(:disabled) {
    --step-fill: rgba(216, 233, 241, 0.94);
    --step-stroke: rgba(255, 255, 255, 0.92);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.24));
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.72);
    outline-offset: 3px;
  }
  @media (max-width: 820px) {
    background: var(--step-fill);
    border-radius: ${tokens.borderRadius.md};
    border: 1px solid var(--step-stroke);
    margin-left: 0;
    padding: 0 18px;
    [data-step-shape="true"] {
      display: none;
    }
  }
`;
export const FormFlowStepLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  z-index: 1;
`;
export const FormFlowStepShape = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
  z-index: 0;
  path {
    fill: var(--step-fill);
    stroke: var(--step-stroke);
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
    transition:
      fill ${tokens.transitions.fast},
      stroke ${tokens.transitions.fast};
    vector-effect: non-scaling-stroke;
  }
`;
export const FormFlowContentPanel = styled.div`
  min-height: 430px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  background: transparent;
  padding: 0;
  box-sizing: border-box;

  @media (max-width: 767px) {
    min-height: 0;
  }
`;

export const FormFlowMobileStepper = styled.ol`
  --mobile-step-line-offset: 4px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  box-sizing: border-box;
`;

export const FormFlowMobileStepItem = styled.li<{
  $active: boolean;
  $completed: boolean;
  $isLast: boolean;
}>`
  --mobile-step-fill: ${({ $active, $completed }) => {
    if ($active) {
      return "rgba(216, 233, 241, 0.94)";
    }

    if ($completed) {
      return "rgba(142, 188, 207, 0.86)";
    }

    return "rgba(67, 112, 132, 0.72)";
  }};
  --mobile-step-stroke: ${({ $active }) =>
    $active
      ? "rgba(255, 255, 255, 0.92)"
      : "rgba(11, 62, 95, 0.96)"};
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  &::after {
    content: "";
    display: ${({ $isLast }) => ($isLast ? "none" : "block")};
    position: absolute;
    z-index: 0;
    top: 64px;
    bottom: 10px;
    left: var(--mobile-step-line-offset);
    width: 1px;
    border-radius: 999px;
    background: var(--mobile-step-fill);
    opacity: 0.78;
  }
`;

export const FormFlowMobileStepHeading = styled.h2`
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
`;

export const FormFlowMobileStepTooltip = styled(Tooltip)`
  width: 100%;
`;

export const FormFlowMobileStepButton = styled.button<{ $active: boolean }>`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 56px;
  margin: 0;
  padding: 6px 10px 6px 0;
  border: 1px solid var(--mobile-step-stroke);
  border-radius: ${tokens.borderRadius.lg};
  background: var(--mobile-step-fill);
  color: ${tokens.colors.blue[900]};
  box-shadow: ${({ $active }) =>
    $active ? "0 4px 14px rgba(0, 0, 0, 0.2)" : "none"};
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  font-family: inherit;
  text-align: left;
  box-sizing: border-box;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition:
    background-color ${tokens.transitions.fast},
    border-color ${tokens.transitions.fast},
    box-shadow ${tokens.transitions.fast};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${tokens.colors.basics[0]};
    outline-offset: 2px;
  }

  &:hover:not(:disabled) {
    background: rgba(216, 233, 241, 0.94);
    border-color: rgba(255, 255, 255, 0.92);
  }
`;

export const FormFlowMobileStepMarker = styled.span`
  position: relative;
  z-index: 1;
  width: 42px;
  height: 42px;
  color: ${tokens.colors.blue[900]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  svg {
    width: 26px;
    height: 26px;
    display: block;
  }
`;

export const FormFlowMobileStepLabel = styled.span`
  min-width: 0;
  font-size: ${tokens.typography.fontSize.md};
  font-weight: ${tokens.typography.fontWeight.semibold};
  line-height: ${tokens.typography.lineHeight.tight};
`;

export const FormFlowMobileStepPosition = styled.span`
  font-size: ${tokens.typography.fontSize.xs};
  font-weight: ${tokens.typography.fontWeight.medium};
  white-space: nowrap;
  opacity: 0.78;
`;

export const FormFlowMobileStepPanel = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin-left: 0;
  padding: 14px 0 28px 12px;
  box-sizing: border-box;
`;
